import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import StarRating from "@/components/base/StarRating";
import useSeo from "@/hooks/useSeo";
import { reviews } from "@/mocks/reviews";

export default function ReviewDetail() {
  const { id } = useParams();
  const review = reviews.find((r) => r.id === id);
  const [saved, setSaved] = useState(false);

  useSeo({
    title: review ? `${review.title} - 激情丛林电影影评` : "影评未找到 - 激情丛林电影",
    description: review ? review.excerpt : "未找到该影评内容。",
    keywords: "激情丛林影评,电影影评,影评详情",
  });

  if (!review) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-32 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-400">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-bold text-foreground-50">未找到这篇影评</h1>
        <p className="mt-2 text-sm text-foreground-400">它可能已被移除，或链接有误。</p>
        <Link
          to="/reviews"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
        >
          <i className="ri-arrow-left-line"></i>
          返回影评列表
        </Link>
      </div>
    );
  }

  const related = reviews
    .filter((r) => r.id !== review.id && r.category === review.category)
    .slice(0, 3);
  const fallback = reviews.filter((r) => r.id !== review.id).slice(0, 3);
  const relatedList = related.length > 0 ? related : fallback;

  return (
    <article className="w-full">
      <div className="relative h-[320px] w-full overflow-hidden md:h-[420px]">
        <img
          src={review.cover}
          alt={`${review.title} 配图`}
          title={`${review.title} - 激情丛林电影影评`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/70 to-background-950/30"></div>
      </div>

      <div className="mx-auto w-full max-w-4xl px-4 pb-4 md:px-6">
        <div className="-mt-24 relative">
          <Breadcrumb items={[{ label: "影评", to: "/reviews" }, { label: "影评详情" }]} />
          <span className="mt-4 inline-flex items-center gap-2 rounded-full border border-accent-500/40 bg-background-950/60 px-3 py-1 font-label text-xs text-accent-400 backdrop-blur">
            <i className="ri-price-tag-3-line"></i>
            {review.category}
          </span>
          <h1 className="mt-4 font-heading text-2xl font-bold leading-tight text-foreground-50 md:text-3xl">
            {review.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-background-800 pb-5">
            <span className="flex items-center gap-2 font-label text-sm text-foreground-300">
              <i className="ri-user-3-line text-accent-500"></i>
              {review.author}
            </span>
            <span className="flex items-center gap-2 font-label text-sm text-foreground-400">
              <i className="ri-newspaper-line text-accent-500"></i>
              {review.source}
            </span>
            <span className="flex items-center gap-2 font-label text-sm text-foreground-400">
              <i className="ri-calendar-line text-accent-500"></i>
              {review.date}
            </span>
            <span className="flex items-center gap-2">
              <StarRating rating={review.rating} showValue />
            </span>
            <button
              type="button"
              onClick={() => setSaved((v) => !v)}
              className={`ml-auto inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-4 py-2 font-label text-sm transition-colors ${
                saved
                  ? "border-accent-500 text-accent-400"
                  : "border-background-600 text-foreground-300 hover:border-accent-400 hover:text-accent-400"
              }`}
            >
              <i className={saved ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
              {saved ? "已收藏" : "收藏"}
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-5 text-[15px] leading-8 text-foreground-300">
          {review.content.map((p) => (
            <p key={p.slice(0, 12)}>{p}</p>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {review.tags.map((t) => (
            <span
              key={t}
              className="rounded-full bg-secondary-100/10 px-3 py-1 font-label text-xs text-secondary-300"
            >
              #{t}
            </span>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-background-800 pt-6">
          <Link
            to="/reviews"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-600 px-5 py-2.5 font-label text-sm text-foreground-100 transition-colors hover:border-primary-500 hover:text-accent-400"
          >
            <i className="ri-arrow-left-line"></i>
            返回影评列表
          </Link>
          <span className="font-label text-xs text-foreground-500">
            收藏为前端演示功能，刷新页面后会清除
          </span>
        </div>
      </div>

      <section className="mx-auto w-full max-w-4xl px-4 py-14 md:px-6">
        <h2 className="mb-6 font-heading text-xl font-bold text-foreground-50">相关影评推荐</h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {relatedList.map((r) => (
            <Link
              key={r.id}
              to={`/review/${r.id}`}
              className="panel group flex h-full flex-col rounded-lg p-5 transition-colors hover:border-primary-500"
            >
              <span className="font-label text-xs text-accent-500">{r.category}</span>
              <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-50 group-hover:text-accent-400">
                {r.title}
              </h3>
              <p className="mt-2 line-clamp-2-custom text-sm text-foreground-400">{r.excerpt}</p>
              <div className="mt-auto flex items-center justify-between pt-4">
                <StarRating rating={r.rating} showValue />
                <span className="font-label text-xs text-foreground-500">{r.date}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}